import { useFormik } from 'formik'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { parseCurrencyToNumber } from '../../helpers'
import { useUpdateVendorDetailsMutation } from '../../redux-toolkits/vendors/vendors.slice'
import { Vendor } from '../../redux-toolkits/vendors/vendors.types'
import { VendorStakeholderPercentageSchema } from '../../validation/validate'
import { Checkbox } from '../inputs/Checkbox'
import { EditInput } from '../inputs/editInput'
import { TextInput } from '../inputs/textInput'

export const VendorStakeholderPercentage = ({
  vendor,
  className = '',
  refetch,
}: {
  vendor?: Vendor
  className?: string
  refetch: () => void
}) => {
  const [editing, setEditing] = useState(false)

  const [updateVendor, { isLoading }] = useUpdateVendorDetailsMutation()

  const hasOverride = vendor?.stakeholderRevenuePercentage != null

  const {
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      useCustomPercentage: hasOverride,
      stakeholderRevenuePercentage: hasOverride
        ? (vendor?.stakeholderRevenuePercentage ?? 0) * 100
        : undefined,
    },
    enableReinitialize: true,
    validationSchema: VendorStakeholderPercentageSchema,
    onSubmit: async (vals) => {
      const resp = await updateVendor({
        id: vendor?._id ?? '',
        data: {
          stakeholderRevenuePercentage: vals.useCustomPercentage
            ? (parseCurrencyToNumber(vals?.stakeholderRevenuePercentage) ?? 0) /
              100
            : null,
        },
      })

      if (resp.data?.success) {
        refetch()
        setEditing(false)
      }
    },
  })

  return (
    <div className={className}>
      <EditInput
        onEditSave={() => {
          handleSubmit()
        }}
        editing={editing}
        onEditClick={() => {
          setEditing(true)
        }}
        onCancel={() => {
          setEditing(false)
        }}
        title="Stakeholder Revenue Percentage"
        loading={isLoading}
      >
        {({ isEditing }) => {
          return isEditing ? (
            <div className="w-full flex flex-col gap-6">
              <Checkbox
                checkStatus={!!values.useCustomPercentage}
                handleChange={(status) => {
                  setFieldValue('useCustomPercentage', status)
                }}
                text="Use custom stakeholder percentage?"
              />

              {values.useCustomPercentage && (
                <div className="flex flex-col gap-4 first:pt-0">
                  <NumericFormat
                    suffix={'%'}
                    prefix={undefined}
                    customInput={TextInput}
                    type="text"
                    name="stakeholderRevenuePercentage"
                    placeholder="Enter percentage (0 - 100)"
                    value={values.stakeholderRevenuePercentage}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={
                      errors.stakeholderRevenuePercentage &&
                      touched.stakeholderRevenuePercentage
                        ? errors.stakeholderRevenuePercentage
                        : ''
                    }
                    hasIcon={false}
                    decimalScale={2}
                    isAllowed={({ floatValue }) =>
                      floatValue === undefined ||
                      (floatValue >= 0 && floatValue <= 100)
                    }
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              {hasOverride
                ? `${(vendor?.stakeholderRevenuePercentage ?? 0) * 100}%`
                : 'System default'}
            </div>
          )
        }}
      </EditInput>
    </div>
  )
}
